import axios from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { IAlertDetails } from '../../../../types/types'
import { CheckConfirm } from '../../../../assets/svgs'

export enum AlertAction {
  RESOLVED = 'resolved',
  USER_RESOLVE = 'user_resolve',
  USER_RESOLVED = 'user_resolved',
  UNRESOLVED = 'unresolved',
  UNRESOLVE = 'unresolve',
  READ = 'read',
  ASSESS = 'assess',
  SNOOZE = 'snooze',
  UNSNOOZE = 'unsnooze',
  HIDE = 'hide',
  DISPLAY = 'display',
}

type alertAction = {
  alertId: string
  alertAction: string
  assessment?: string
  duration?: number
}

export const useUpdateDeviceAlertStatus = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (variables: alertAction) => {
      let patchData: {
        alert_action: string
        assessment?: string
        duration?: number
      } = { alert_action: variables.alertAction }

      switch (variables.alertAction) {
        case AlertAction.USER_RESOLVED:
          patchData = {
            alert_action: AlertAction.USER_RESOLVE,
          }
          break
        case AlertAction.UNRESOLVED:
          patchData = {
            alert_action: AlertAction.UNRESOLVE,
          }
          break
        case AlertAction.ASSESS:
          patchData = {
            assessment: variables.assessment,
            alert_action: AlertAction.ASSESS,
          }
          break
        case AlertAction.SNOOZE:
          patchData = {
            duration: variables.duration,
            alert_action: AlertAction.SNOOZE,
          }
          break
      }
      return axios
        .patch(`/alerts/${variables.alertId}`, patchData)
        .then((res) => res.data)
    },
    {
      // BEFORE mutation happens, do an optimistic update
      onMutate: (variables: alertAction) => {
        // cancel the ongoing query
        queryClient.cancelQueries('deviceAlert')

        // query the client based on alert id
        const previousDeviceAlertStatus =
          queryClient.getQueryData<IAlertDetails>([
            'deviceAlert',
            variables.alertId,
          ])

        // Do not optimistically update for read value
        if (variables.alertAction === AlertAction.READ) {
          return { previousDeviceAlertStatus }
        }

        // if an alert exists with the alert id,
        // reset the corresponding deviceAlertStatus to a new value
        if (previousDeviceAlertStatus) {
          let resolutionStatus = ''
          if (variables.assessment || variables.duration) {
            resolutionStatus = previousDeviceAlertStatus.alert.resolution_status
          } else {
            resolutionStatus = variables.alertAction
          }
          queryClient.setQueryData(['deviceAlert', variables.alertId], {
            ...previousDeviceAlertStatus,
            alert: {
              ...previousDeviceAlertStatus.alert,
              resolution_status: resolutionStatus,
            },
          })
        }

        // add previous alert status to context
        return { previousDeviceAlertStatus }
      },
      onError: (error, variables, context) => {
        // if there is an error
        if (variables.alertAction === AlertAction.READ) return

        // if there is a previous value on the context
        // revert back to previous device alert status stored on the context
        if (context?.previousDeviceAlertStatus) {
          queryClient.setQueryData(['deviceAlert', variables.alertId], {
            ...context.previousDeviceAlertStatus,
          })
        }
        toast.error(
          `Uh oh. There was an issue updating device alert status. Please contact us if the problem persists.`
        )
        console.log(error)
      },
      onSuccess: (data, variables) => {
        if (variables.alertAction === AlertAction.READ) return
        let successMessage = 'Alert status was successfully updated.'
        if (variables.assessment) {
          successMessage =
            'Alert status and assessment are successfully updated.'
        }
        if (variables.duration) {
          successMessage = 'Alert is snoozed successfully.'
        }
        toast.success(successMessage, {
          icon: <CheckConfirm />,
        })
      },
      onSettled: (data, error, variables) => {
        // invalidate current query
        if (error) return
        queryClient.invalidateQueries(['deviceAlert', variables.alertId])
        queryClient.invalidateQueries('deviceNotifications')
      },
    }
  )
}
